/**
 * The product pages list's loads: which response the section shows, and what
 * each caller of a load gets back. Free of React and axios so the rule is
 * tested on its own (loadSequence.test.ts); useProductPages hands it the list
 * GET and mirrors its rows into state.
 *
 * The rule it replaces (a ticket per load, MODAL/useProductAutopilot.ts:21-46)
 * dropped any response a newer load or a local patch overtook and resolved its
 * caller with null. Two things broke on that: a write's awaited re-read could
 * be dropped with no load after it, so the list stayed stale (Pause scoring on
 * B, then Generate Product Object on A: B still looked active); and a failed
 * Score now released its lock early when a poll overtook its re-read. Now:
 *
 *   - A response is shown unless a newer one is already on screen, or it read
 *     the server before the last local patch (showing it could undo the patch).
 *   - A caller whose response isn't shown gets nothing older: a newer load's
 *     rows, the moment one lands (its own response is then ignored); or, when
 *     a patch made its response stale, the result of a load that started
 *     after the patch — one already out, or a follow-up started for it. So a
 *     write's awaited re-read resolves only once a load that started after
 *     the write has landed (with its rows) or failed.
 *   - A failure keeps the rows and resolves null, unless a newer load is out:
 *     then its caller gets that one's result. A hard load's error (the
 *     section's "Couldn't load product pages") waits until no load is out, so
 *     an older load still out can land instead.
 *   - A patch sends nothing. Only a load that was out during it and lands after
 *     it, with no newer load out, starts a follow-up.
 *
 * Start order stands for read order: a load that started later read the
 * server later.
 */

interface LoadSequenceOptions<T> {
  /** One GET of the list. */
  fetch: () => Promise<T[]>;
  /** The rows changed: `landed` = a load's rows (the list is no longer loading), else a local patch. */
  onRows: (rows: T[], landed: boolean) => void;
  /**
   * A hard load (the first load, Try again) ended with nothing shown: it and
   * every load after it failed, and no load is out. `err` is the last failure.
   */
  onHardFail: (err: unknown) => void;
}

interface LoadSequence<T> {
  /**
   * Starts a load (`hard` = the first load or Try again). Resolves to rows no
   * older than the call — its own, or a newer load's — or null on a failure.
   * Never rejects.
   */
  load: (hard?: boolean) => Promise<T[] | null>;
  /**
   * A local write landed: apply it to the rows. Sends nothing; a load out now
   * read the server before the write, so it can't undo this (see above).
   */
  patch: (update: (rows: T[]) => T[]) => void;
  /** The rows as shown: the last landed load plus later patches. Null until a load lands. */
  rows: () => T[] | null;
  /** Unmount: nothing more is sent or shown. Loads still out resolve null at once; a load asked for now sends nothing. */
  dispose: () => void;
}

type Answer<T> = T[] | null | Promise<T[] | null>;

interface Out<T> {
  /** What the caller of `load` gets. */
  promise: Promise<T[] | null>;
  resolve: (answer: Answer<T>) => void;
  /** The caller already has its answer (a newer load landed first); the GET is still out. */
  answered: boolean;
}

export function createLoadSequence<T>({ fetch, onRows, onHardFail }: LoadSequenceOptions<T>): LoadSequence<T> {
  let rows: T[] | null = null;
  /** The newest ticket handed out. */
  let last = 0;
  /** The ticket whose response the rows are (0: none yet). */
  let shown = 0;
  /** Loads with a ticket up to this one read the server before the last patch. */
  let patchedAt = 0;
  /** A hard load is out, and nothing has landed since. */
  let hardOut = false;
  let disposed = false;
  /** Loads whose GET is out, by ticket. */
  const out = new Map<number, Out<T>>();

  const answer = (load: Out<T> | undefined, value: Answer<T>) => {
    if (!load || load.answered) return;
    load.answered = true;
    load.resolve(value);
  };

  const newestOut = (): number => {
    let newest = 0;
    out.forEach((_load, ticket) => {
      if (ticket > newest) newest = ticket;
    });
    return newest;
  };

  const landed = (ticket: number, next: T[]) => {
    const load = out.get(ticket);
    out.delete(ticket);
    if (disposed) return;
    // A newer load already landed (and answered this caller): its rows, and
    // any patch since, are newer than these.
    if (ticket <= shown) {
      answer(load, rows);
      return;
    }
    // Read before the last patch: these rows could undo it. Hand over a load
    // that started after the patch — the newest one out, or a follow-up.
    if (ticket <= patchedAt) {
      const newer = newestOut();
      answer(load, newer > patchedAt ? (out.get(newer) as Out<T>).promise : start(false));
      return;
    }
    shown = ticket;
    hardOut = false;
    rows = next;
    onRows(next, true);
    answer(load, next);
    // Every older load still out is overtaken: its caller gets these rows
    // now, and its response is ignored when it lands.
    out.forEach((older, t) => {
      if (t < ticket) answer(older, next);
    });
  };

  const failed = (ticket: number, err: unknown) => {
    const load = out.get(ticket);
    out.delete(ticket);
    if (disposed) return;
    if (ticket <= shown) {
      answer(load, rows);
      return;
    }
    const newer = newestOut();
    if (newer > ticket) {
      answer(load, (out.get(newer) as Out<T>).promise);
      return;
    }
    // Nothing newer: a failure. The rows stay as they are (the next poll or
    // write re-reads); a hard load's error waits for any older load still out.
    if (hardOut && out.size === 0) {
      hardOut = false;
      onHardFail(err);
    }
    answer(load, null);
  };

  const start = (hard: boolean): Promise<T[] | null> => {
    if (disposed) return Promise.resolve(null);
    last += 1;
    const ticket = last;
    if (hard) hardOut = true;
    let resolve: (answer: Answer<T>) => void = () => undefined;
    const promise = new Promise<T[] | null>((r) => {
      resolve = r;
    });
    out.set(ticket, { promise, resolve, answered: false });
    let request: Promise<T[]>;
    try {
      request = fetch();
    } catch (e) {
      request = Promise.reject(e);
    }
    request.then(
      (next) => landed(ticket, next),
      (err) => failed(ticket, err),
    );
    return promise;
  };

  return {
    load: (hard = false) => start(hard),
    patch: (update) => {
      if (disposed) return;
      patchedAt = last;
      if (!rows) return;
      rows = update(rows);
      onRows(rows, false);
    },
    rows: () => rows,
    dispose: () => {
      disposed = true;
      // The callers go on at once (and find the hook unmounted); the GETs
      // still out land nowhere.
      out.forEach((load) => answer(load, null));
    },
  };
}
